import { Timestamp } from 'firebase/firestore';
import type { WorkoutSession } from '../../types';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { formatDuration, getDurationMinutes } from '@/shared/utils/dates';

interface SessionSummaryProps {
  session: WorkoutSession;
}

export function SessionSummary({ session }: SessionSummaryProps) {
  const completedItems = session.workoutSnapshot.items.filter(item => !item.skipped);
  const skippedItems = session.workoutSnapshot.items.filter(item => item.skipped);

  // Calculate duration
  const durationMinutes = getDurationMinutes(session.startedAt, session.completedAt);
  const formattedDuration = formatDuration(durationMinutes);

  // Format date
  const completedDate = session.completedAt
    ? (session.completedAt as Timestamp).toDate().toLocaleDateString([], {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  const completedTime = session.completedAt
    ? (session.completedAt as Timestamp).toDate().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <Card variant="elevated" padding="lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Workout Summary
          </h2>
          <Badge variant={session.status === 'completed' ? 'success' : 'warning'}>
            {session.status === 'completed' ? 'Completed' : 'Abandoned'}
          </Badge>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 rounded-lg bg-gray-50 dark:bg-white/5">
            <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">
              {completedItems.length}
            </p>
            <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
              Exercises Completed
            </p>
          </div>

          <div className="text-center p-4 rounded-lg bg-gray-50 dark:bg-white/5">
            <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">
              {completedItems.reduce((total, item) => total + item.sets.length, 0)}
            </p>
            <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
              Sets Completed
            </p>
          </div>

          <div className="text-center p-4 rounded-lg bg-gray-50 dark:bg-white/5">
            <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">
              {formattedDuration}
            </p>
            <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
              Duration
            </p>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-white/10">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-slate-400">Completed on</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {completedDate} at {completedTime}
            </span>
          </div>
        </div>
      </Card>

      {/* Exercises Completed */}
      {completedItems.length > 0 && (
        <Card variant="elevated" padding="lg">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Exercises Completed
          </h3>
          <div className="space-y-4">
            {completedItems.map((item, idx) => (
              <div
                key={item.id}
                className="p-4 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {idx + 1}. {item.exerciseName}
                    </p>
                    {item.movementPattern && (
                      <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                        {item.movementPattern.replace(/_/g, ' ')}
                      </p>
                    )}
                  </div>
                  <Badge variant="info" className="text-xs">
                    {item.sets.length} {item.sets.length === 1 ? 'set' : 'sets'}
                  </Badge>
                </div>

                {/* Sets breakdown */}
                <div className="space-y-2">
                  {item.sets.map((set) => (
                    <div
                      key={set.index}
                      className="flex items-center justify-between text-sm py-2 px-3 rounded bg-white dark:bg-slate-900/50"
                    >
                      <span className="text-gray-600 dark:text-slate-400">
                        Set {set.index + 1}
                      </span>
                      <div className="flex items-center gap-4">
                        {set.weight !== undefined && (
                          <span className="text-gray-900 dark:text-white font-medium">
                            {set.weight} {item.equipmentTags?.includes('bodyweight') ? '' : 'lbs'}
                          </span>
                        )}
                        {set.repsDone !== undefined && (
                          <span className="text-gray-900 dark:text-white font-medium">
                            {set.repsDone} reps
                          </span>
                        )}
                        {set.weight === undefined && set.repsDone === undefined && (
                          <span className="text-gray-500 dark:text-slate-500 italic">
                            No data logged
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Item notes */}
                {item.itemNotes && (
                  <div className="mt-3 p-3 rounded bg-white dark:bg-slate-900/50">
                    <p className="text-xs text-gray-600 dark:text-slate-400 font-medium mb-1">
                      Notes
                    </p>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {item.itemNotes}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Skipped Exercises */}
      {skippedItems.length > 0 && (
        <Card variant="elevated" padding="lg">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Skipped Exercises
          </h3>
          <div className="space-y-3">
            {skippedItems.map((item) => (
              <div
                key={item.id}
                className="p-4 rounded-lg border border-warning-200 dark:border-warning-500/20 bg-warning-50 dark:bg-warning-500/5"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {item.exerciseName}
                    </p>
                    {item.skipReason && (
                      <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                        Reason: {item.skipReason}
                      </p>
                    )}
                  </div>
                  <Badge variant="warning" className="text-xs">
                    Skipped
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
